/* What a reader is told about automatic profiling, derived from one payload.
 *
 * **The whole reason this is a model rather than JSX**: three of the four states are absences, and
 * collapsing any pair of them makes a working setting and a stalled one look alike. §9's
 * absence-is-not-zero rule, applied to a queue.
 *
 * - `unconfigured` — nobody has chosen. The platform's default, reported *as* a default.
 * - `off` — somebody chose not to. A decision, not a default.
 * - `idle` — on, with nothing outstanding. The steady state of a stable source.
 * - `working` — on, with work queued or running.
 *
 * `needsAttention` is separate from all four, because a queue can be healthily draining *and* have
 * exhausted jobs somebody must look at. One badge cannot say both.
 */

import type { AutoProfile, AutoProfileQueueCounts } from '@/shared/api/generated/catalog';

export type AutoProfilePresence =
  | { readonly kind: 'unconfigured' }
  | { readonly kind: 'off' }
  | { readonly kind: 'idle' }
  | { readonly kind: 'working'; readonly outstanding: number };

export function presenceOf(profile: AutoProfile): AutoProfilePresence {
  if (!profile.configured) {
    return { kind: 'unconfigured' };
  }
  if (!profile.enabled) {
    /* **Off is reported even with work outstanding**, and that ordering is deliberate. Switching
       off retires the backlog server-side, so a non-zero count here is history rather than pending
       work — reporting "working" would tell a reader scans are about to happen when the control
       says they are not. */
    return { kind: 'off' };
  }
  const outstanding = profile.queue.outstanding;
  return outstanding > 0 ? { kind: 'working', outstanding } : { kind: 'idle' };
}

/** Whether any job has exhausted its attempts. **The only figure that means somebody should look.**
 *
 * `pending`, `retrying` and `running` all resolve by themselves; `failed` does not — the queue
 * stops retrying once attempts are spent, so those datasets stay unprofiled until a person acts.
 * Deliberately not folded into `presenceOf`: a source can be draining a healthy backlog and have
 * four exhausted jobs at the same time, and one badge cannot carry both facts.
 */
export function needsAttention(counts: AutoProfileQueueCounts): boolean {
  return counts.failed > 0;
}

/** Whether there is anything the retry control could act on. */
export function canRetry(counts: AutoProfileQueueCounts): boolean {
  return counts.failed > 0;
}

export type QueueTone = 'ok' | 'info' | 'warn' | 'neutral';

/** The tone for the whole card.
 *
 * A **total map on the four presences** rather than a `switch` with a `default:`, so adding a fifth
 * presence is a compile error in the file whose first paragraph is "the four states must stay
 * four". §9 records `verdictTone`'s `default:` rendering an unknown verdict in the *permitted*
 * tone, which is the failure this shape prevents.
 */
const TONE: Record<AutoProfilePresence['kind'], QueueTone> = {
  unconfigured: 'neutral',
  off: 'neutral',
  idle: 'ok',
  working: 'info',
};

export function toneOf(
  presence: AutoProfilePresence,
  counts: AutoProfileQueueCounts,
): QueueTone {
  /* Attention wins over the presence tone, because an exhausted job is the one thing here a reader
     has to act on — and a green "up to date" badge beside four permanently unprofiled datasets is
     the screen contradicting itself. */
  return needsAttention(counts) ? 'warn' : TONE[presence.kind];
}
